import { Controller } from "@hotwired/stimulus";

const STORAGE_KEY = "whats-new-state";
const LEGACY_STORAGE_KEY = "whats-new-seen";

export default class extends Controller {
  static targets = ["panel", "dot", "item", "list", "empty", "backdrop"];
  static values = { entryIds: Array };

  connect() {
    const state = this.readState();
    this.itemTargets
      .filter((el) => state.dismissed.includes(el.dataset.whatsNewId))
      .forEach((el) => el.remove());
    this.syncEmpty();
    this.syncDot();
    this.boundClose = this.closeOnOutsideClick.bind(this);
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose);
  }

  toggle() {
    this.panelTarget.hidden ? this.open() : this.close();
  }

  open() {
    this.panelTarget.hidden = false;
    if (this.hasBackdropTarget) this.backdropTarget.hidden = false;
    requestAnimationFrame(() => {
      this.panelTarget.classList.add("whats-new__panel--open");
      if (this.hasBackdropTarget) this.backdropTarget.classList.add("whats-new__backdrop--open");
    });
    document.addEventListener("click", this.boundClose);

    const state = this.readState();
    this.entryIdsValue.forEach((id) => {
      if (!state.dismissed.includes(id) && !state.seen.includes(id)) state.seen.push(id);
    });
    this.writeState(state);
    this.syncDot();
  }

  removeItem(e) {
    e.stopPropagation();
    const id = e.currentTarget.closest("[data-whats-new-id]").dataset.whatsNewId;
    const state = this.readState();
    if (!state.dismissed.includes(id)) state.dismissed.push(id);
    this.writeState(state);

    const item = this.itemTargets.find((el) => el.dataset.whatsNewId === id);
    if (item) {
      const height = item.offsetHeight;
      item.style.height = `${height}px`;
      item.style.overflow = "hidden";
      requestAnimationFrame(() => item.classList.add("whats-new__item--exiting"));
      item.addEventListener(
        "transitionend",
        (e) => {
          if (e.propertyName !== "height") return;
          item.remove();
          this.syncEmpty();
          this.syncDot();
        }
      );
      requestAnimationFrame(() => requestAnimationFrame(() => { item.style.height = "0px"; }));
    } else {
      this.syncEmpty();
      this.syncDot();
    }
  }

  syncEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.hidden = this.itemTargets.length > 0;
  }

  readState() {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        return { seen: parsed.seen || [], dismissed: parsed.dismissed || [] };
      } catch {
        // fall through to legacy migration
      }
    }

    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    return { seen: legacy ? [legacy] : [], dismissed: [] };
  }

  writeState(state) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  close() {
    this.panelTarget.classList.remove("whats-new__panel--open");
    if (this.hasBackdropTarget) this.backdropTarget.classList.remove("whats-new__backdrop--open");
    document.removeEventListener("click", this.boundClose);

    const duration = parseFloat(getComputedStyle(this.panelTarget).transitionDuration) * 1000;
    if (duration > 0) {
      this.panelTarget.addEventListener("transitionend", () => { this.panelTarget.hidden = true; }, { once: true });
    } else {
      this.panelTarget.hidden = true;
    }
    if (this.hasBackdropTarget) this.backdropTarget.hidden = true;
  }

  closeOnOutsideClick(e) {
    if (!this.element.contains(e.target)) this.close();
  }

  syncDot() {
    const state = this.readState();
    const hasActive = this.entryIdsValue.some((id) => !state.dismissed.includes(id));
    if (this.hasDotTarget) this.dotTarget.hidden = !hasActive;
  }
}
